import React, { useEffect, useRef, useState } from "react"
import VolumeCalculator from "./components/VolumeCalculator"
import jnjLogo from "./assets/images/JNJ_Logo_Stacked_Red_RGB.svg"
import "katex/dist/katex.min.css"
import "./App.css"

const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const INACTIVITY_TIMEOUT_MINUTES = 15

const INACTIVITY_TIMEOUT_MS =
  INACTIVITY_TIMEOUT_MINUTES * SECONDS_PER_MINUTE * MS_PER_SECOND

export default function App() {
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false)
  const [timedOut, setTimedOut] = useState(false)

  const acceptButtonRef = useRef(null)
  const disclaimerModalRef = useRef(null)

  const timeoutModalRef = useRef(null)
  const refreshButtonRef = useRef(null)

  const inactivityTimerRef = useRef(null)

  const trapFocus = (modalEl, initialFocusEl) => {
    if (!modalEl) return () => {}

    const focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    const focusableElements = modalEl.querySelectorAll(focusableSelectors)

    const firstEl = focusableElements[0]
    const lastEl = focusableElements[focusableElements.length - 1]

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return

      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl?.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl?.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    if (initialFocusEl?.current) initialFocusEl.current.focus()
    else firstEl?.focus()

    return () => document.removeEventListener("keydown", handleKeyDown)
  }

  useEffect(() => {
    if (disclaimerAccepted) return
    const modal = disclaimerModalRef.current
    return trapFocus(modal, acceptButtonRef)
  }, [disclaimerAccepted])

  useEffect(() => {
    if (!timedOut) return
    const modal = timeoutModalRef.current
    return trapFocus(modal, refreshButtonRef)
  }, [timedOut])

  const handleAccept = () => {
    setDisclaimerAccepted(true)
  }

  const refreshApp = () => {
    window.location.reload()
  }

  useEffect(() => {
    if (!disclaimerAccepted) return
    if (timedOut) return
    const clearTimer = () => {
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }
    }

    const startTimer = () => {
      clearTimer()
      inactivityTimerRef.current = window.setTimeout(() => {
        setTimedOut(true)
      }, INACTIVITY_TIMEOUT_MS)
    }

    const handleActivity = () => {
      startTimer()
    }

    startTimer()

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "click",
    ]

    events.forEach(evt =>
      window.addEventListener(evt, handleActivity, { passive: true })
    )

    return () => {
      clearTimer()
      events.forEach(evt => window.removeEventListener(evt, handleActivity))
    }
  }, [disclaimerAccepted, timedOut])

  const isLocked = !disclaimerAccepted || timedOut

  return (
    <>
      <div
        className={`page app-shell ${isLocked ? "is-locked" : ""}`}
        aria-hidden={isLocked}
      >
        <header className="header app-header">
          <div className="header-text">
            <h1>Tumor Volume Calculator</h1>
            <p className="subtitle">
              Enter longest diameter (<strong>LD</strong>) and short-axis diameter (
              <strong>SAD</strong>) to compute the estimated tumor volume. Always
              refer to the trial protocol when utilizing this tool.
            </p>
          </div>
        </header>

        <main className="main">
          <VolumeCalculator />
        </main>

        <footer className="footer">
          <div className="footer-top">
            <img
              src={jnjLogo}
              alt="Johnson & Johnson"
              className="footer-logo"
            />

            <small className="footer-note">
              If an issue with the Tumor Volume Calculator is noted, stop use and notify the Site Manager (SM) and/or Field Clinical
              Manager (FCM). Refer to the trial protocol for more instructions.
            </small>
          </div>

          <div className="footer-links">
            <a
              href="https://www.jnj.com/privacy-center/en-us/privacy-statement?s=00000195-2026-debe-abb7-a336ba8c0000&p=00000195-202a-ddef-a3f7-213b3e7b0000"
              className="privacy-link"
              target="_blank"
              rel="noreferrer"
            >
              Privacy
            </a>
            <a
              href="https://www.jnj.com/legal-notice"
              className="terms-link"
              target="_blank"
              rel="noreferrer"
            >
              Legal Terms
            </a>
          </div>
        </footer>
      </div>

      {!disclaimerAccepted && (
        <div className="disclaimer-overlay">
          <div
            className="disclaimer-modal"
            ref={disclaimerModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="disclaimer-title"
            aria-describedby="disclaimer-body"
          >
            <h2 id="disclaimer-title">Disclaimer</h2>

            <div id="disclaimer-body" className="disclaimer-body">
              <p>
                This Tumor Volume Calculator is designed to estimate tumor volume for patients undergoing intratumoral oncology treatment within the context of a clinical trial, using clinician-entered measurements and a modified sphere volume equation. It is not intended to calculate dosage or determine tumor size.
                Please note that this tool is intended only to provide secondary confirmation to the clinician’s manual calculation and is not a substitute for clinical judgment, expert interpretation, or the clinical decision-making process.
                The Tumor Volume Calculator is intended only for use within approved clinical trials by qualified healthcare professionals in the United States, Australia, United Kingdom, Spain, and France.
              </p>
              <p>
                By clicking <strong>Accept</strong>, you acknowledge your role as a qualified healthcare professional and agree to use the calculator strictly for its intended purpose, within the specified regions and clinical trial context.
              </p>
            </div>

            <div className="disclaimer-actions">
              <button
                ref={acceptButtonRef}
                className="cta-red-oval"
                onClick={handleAccept}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {timedOut && (
        <div className="timeout-overlay">
          <div
            className="timeout-modal"
            ref={timeoutModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="timeout-title"
            aria-describedby="timeout-body"
          >
            <h2 id="timeout-title">Session Timed Out</h2>

            <div id="timeout-body" className="timeout-body">
              <p>
                For security reasons, your session has timed out due to 15 minutes of inactivity.
              </p>
              <p>
                Select <strong>Refresh</strong> to reload the application and continue.
              </p>
            </div>

            <div className="timeout-actions">
              <button
                ref={refreshButtonRef}
                className="cta-red-oval"
                onClick={refreshApp}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}